import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';

const DEEP_LINK_SCHEME = 'clawdvoice://auth';

export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('processing'); // processing | success | error
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const handleCallback = async () => {
      try {
        if (!supabase) {
          throw new Error('Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to your Vercel environment variables.');
        }

        // Extract the PKCE authorization code from the callback URL
        const code = new URLSearchParams(window.location.search).get('code');
        if (!code) {
          throw new Error('No authorization code found in callback URL.');
        }

        // Exchange the code for a session
        const { data, error: exchangeError } =
          await supabase.auth.exchangeCodeForSession(code);
        if (exchangeError) throw exchangeError;
        if (!data.session) throw new Error('No session returned from Supabase.');

        redirect(data.session);
      } catch (err) {
        console.error('Auth callback error:', err);
        setError(err.message || 'Authentication failed.');
        setStatus('error');
      }
    };

    const redirect = (session) => {
      const { access_token, refresh_token } = session;
      const deepLink = `${DEEP_LINK_SCHEME}?access_token=${encodeURIComponent(access_token)}&refresh_token=${encodeURIComponent(refresh_token)}`;

      setStatus('success');

      // Small delay so the user sees the success state before the redirect
      setTimeout(() => {
        window.location.href = deepLink;
      }, 800);
    };

    handleCallback();
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
      <div className="text-center max-w-sm px-6">
        {/* FN Logo */}
        <div className="flex items-center justify-center gap-2 mb-10">
          <span className="fn-key text-sm px-2 py-1">FN</span>
          <span
            style={{ fontFamily: 'Manrope, sans-serif' }}
            className="text-xl font-bold tracking-tight text-zinc-900"
          >
            ClawdVoice
          </span>
        </div>

        {status === 'processing' && (
          <>
            <div className="flex items-center justify-center gap-1.5 mb-6">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-2 h-2 bg-zinc-900 rounded-full animate-bounce"
                  style={{ animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </div>
            <h2
              style={{ fontFamily: 'Manrope, sans-serif' }}
              className="text-xl font-semibold text-zinc-900 mb-2"
            >
              Signing you in...
            </h2>
            <p className="text-sm text-zinc-400">
              Verifying your account with Google.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-12 h-12 bg-green-500 rounded-sm flex items-center justify-center mx-auto mb-6">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2
              style={{ fontFamily: 'Manrope, sans-serif' }}
              className="text-xl font-semibold text-zinc-900 mb-2"
            >
              Authenticated
            </h2>
            <p className="text-sm text-zinc-400">
              Launching ClawdVoice and transferring your session...
            </p>
            <p
              data-testid="deep-link-status"
              className="mt-4 text-xs font-mono text-zinc-300"
            >
              Redirecting via clawdvoice://
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-12 h-12 bg-red-500 rounded-sm flex items-center justify-center mx-auto mb-6">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2
              style={{ fontFamily: 'Manrope, sans-serif' }}
              className="text-xl font-semibold text-zinc-900 mb-2"
            >
              Sign-in failed
            </h2>
            <p
              data-testid="auth-error-message"
              className="text-sm text-zinc-400 mb-6"
            >
              {error}
            </p>
            <button
              onClick={() => window.location.href = '/'}
              className="bg-zinc-900 text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-zinc-700 transition-all active:scale-95"
            >
              Back to home
            </button>
          </>
        )}
      </div>
    </div>
  );
}
